import React, { useCallback } from 'react';
import { Box, CssBaseline, ThemeProvider, Typography, LinearProgress } from '@mui/material';
import QueryProvider from './providers/QueryProvider';
import { AppProvider, useAppContext } from './contexts/AppContext';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import Dashboard from './components/Dashboard';
import UnifiedProjectView from './components/UnifiedProjectView';
import HorizontalProjectList from './components/HorizontalProjectList';
import Header from './components/Header';
import ProjectEditor from './components/ProjectEditor';
import EmptyState from './components/EmptyState';
import AdminPage from './components/admin/AdminPage';
import ResizableDivider from './components/ResizableDivider';
import theme from './theme/theme';

// Main App component - only handles providers
const App: React.FC = () => {
  return (
    <QueryProvider>
      <AppProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </AppProvider>
    </QueryProvider>
  );
};

// App content component - handles actual UI logic with AppContext
const AppContent: React.FC = () => {
  const { 
    state, 
    selectProject, 
    createProject, 
    saveProject, 
    deleteProject,
    toggleAdminPage,
    toggleDashboardView
  } = useAppContext();

  const { 
    currentProject, 
    projects, 
    loading,
    uiState: { showAdminPage, useCubaDashboard }
  } = state;

  // Render loading state
  if (loading) {
    return (
      <Box 
        p={3} 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="h5" color="primary">
          Loading Requirements Tracker...
        </Typography>
        <LinearProgress sx={{ width: '50%', mx: 'auto' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh'
    }}>
      <Header 
        projectName={currentProject?.name || 'Requirements Tracker'} 
        onAdminClick={toggleAdminPage}
        onDashboardToggle={toggleDashboardView}
        useCubaDashboard={useCubaDashboard}
      />
      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', // Changed from 'hidden' to 'auto' to enable scrolling
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        {showAdminPage ? (
          <AdminPage onBack={toggleAdminPage} />
        ) : currentProject ? (
          <>
            {/* Horizontal Project List */}
            <HorizontalProjectList
              projects={projects}
              currentProject={currentProject}
              onSelectProject={selectProject}
              onCreateProject={createProject}
              onDeleteProject={deleteProject}
            />
            
            <UnifiedProjectView 
              project={currentProject} 
              onSave={saveProject}
              useCubaDashboard={useCubaDashboard}
            />
          </>
        ) : (
          <EmptyState onCreateProject={createProject} />
        )}
      </Box>
    </Box>
  );
};

export default App;
